import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { StatusBadge } from '../components/ui/status-badge';
import { toast } from '../components/ui/use-toast';
import { useConfirm } from '../components/ui/confirm-dialog';
import { useTheme } from '../contexts/ThemeContext';
import { AuthAPI, User } from '../lib/api';
import { User as UserIcon, Lock, Palette, LogOut, Moon, Sun, ShieldCheck } from 'lucide-react';

export default function SettingsPage() {
  const { theme, toggleTheme } = useTheme();
  const confirm = useConfirm();

  const [user, setUser] = useState<User | null>(null);
  const [loadingUser, setLoadingUser] = useState(true);

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await AuthAPI.getCurrentUser();
        if (mounted && res.success && res.data) setUser(res.data);
      } catch {
        /* non-fatal */
      } finally {
        if (mounted) setLoadingUser(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast({ title: 'Password too short', description: 'Use at least 8 characters.', variant: 'destructive' });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({ title: 'Passwords do not match', description: 'Re-enter the new password.', variant: 'destructive' });
      return;
    }
    try {
      setSavingPassword(true);
      const res = await AuthAPI.changePassword(oldPassword, newPassword);
      if (res.success) {
        toast({ title: 'Password updated', variant: 'success' });
        setOldPassword(''); setNewPassword(''); setConfirmPassword('');
      } else {
        toast({ title: 'Could not update password', description: res.message || 'Try again.', variant: 'destructive' });
      }
    } catch (err: any) {
      toast({ title: 'Could not update password', description: err?.message || 'Try again.', variant: 'destructive' });
    } finally {
      setSavingPassword(false);
    }
  };

  const handleLogout = async () => {
    const ok = await confirm({ title: 'Log out?', description: 'You will need to sign in again.', confirmText: 'Log out' });
    if (!ok) return;
    try { await AuthAPI.logout(); } catch { /* ignore */ }
    localStorage.removeItem('auth_token');
    window.location.href = '/login';
  };

  return (
    <div className="mx-auto w-full max-w-3xl space-y-6 p-4 sm:p-6">
      <div>
        <h1 className="text-2xl font-bold sm:text-3xl">Settings</h1>
        <p className="text-sm text-muted-foreground">Manage your account, security and appearance.</p>
      </div>

      {/* Account */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <UserIcon className="h-5 w-5 text-primary" /> Account
          </CardTitle>
          <CardDescription>Your admin profile.</CardDescription>
        </CardHeader>
        <CardContent>
          {loadingUser ? (
            <div className="space-y-2">
              <Skeleton className="h-5 w-1/2" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          ) : user ? (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="font-semibold">{user.name || '—'}</div>
                <div className="text-sm text-muted-foreground">{user.email}</div>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={user.role} />
                <StatusBadge status={user.isActive ? 'active' : 'inactive'} />
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Could not load your profile.</p>
          )}
        </CardContent>
      </Card>

      {/* Security */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Lock className="h-5 w-5 text-primary" /> Change Password
          </CardTitle>
          <CardDescription>Use a strong password you don't use elsewhere.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="old">Current password</Label>
              <Input id="old" type="password" value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} autoComplete="current-password" required />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="new">New password</Label>
                <Input id="new" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} autoComplete="new-password" required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm">Confirm new password</Label>
                <Input id="confirm" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} autoComplete="new-password" required />
              </div>
            </div>
            <div className="flex justify-end">
              <Button type="submit" disabled={savingPassword}>
                <ShieldCheck className="mr-2 h-4 w-4" />
                {savingPassword ? 'Updating…' : 'Update password'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Appearance */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Palette className="h-5 w-5 text-primary" /> Appearance
          </CardTitle>
          <CardDescription>Switch between light and dark mode.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm">
              {theme === 'dark' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
              <span className="capitalize">{theme} mode</span>
            </div>
            <Button variant="outline" onClick={toggleTheme}>
              Switch to {theme === 'dark' ? 'light' : 'dark'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Session */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <LogOut className="h-5 w-5 text-destructive" /> Session
          </CardTitle>
          <CardDescription>Sign out of the admin portal on this device.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="destructive" onClick={handleLogout}>
            <LogOut className="mr-2 h-4 w-4" /> Log out
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
